import type { SiteDTO } from 'src/types/dto/site.dto';
import type { ProjectDTO } from 'src/types/dto/project.dto';
import type { CompetencyDTO } from 'src/types/dto/competency.dto';
import type { ActivityDTO, ActivityType } from 'src/types/dto/activity.dto';
import type { ProjectAuditTypeDTO } from 'src/types/dto/project-standard.dto';

import dayjs from 'dayjs';
import { mutate } from 'swr';
import { z as zod } from 'zod';
import utc from 'dayjs/plugin/utc';
import { useForm } from 'react-hook-form';
import { useMemo, useEffect, useState, type FocusEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Modal from '@mui/material/Modal';
import { Alert, Button, Chip, Switch } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';

import { useListForFilter } from 'src/hooks/use-list-for-filter';

import axios, { endpoints } from 'src/utils/axios';

import { useGetVisit } from 'src/actions/visit';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';
import { SplashScreen } from 'src/components/loading-screen';
import { CustomLabelWithLink } from 'src/components/custom-label-with-link';
import { VirtualizedAutocomplete, Option } from 'src/components/virtualized-autocomplete';
import {
  ActivityDependenciesField,
  type ActivityDependencyFormValue,
} from './components/activity-dependencies-field';

import { useAuthContext } from 'src/auth/hooks';
import { AnalyticsEvent, trackEvent } from 'src/lib/analytics';
import { useCanUpdateOwnedActivity } from 'src/hooks/use-can-update-owned-activity';

import {
  ActivityStatus,
  Priority,
  PRIORITY_MAP,
  PRIORITY_VALUES_MAP,
} from 'src/types/dto/activity.dto';

import { SchedulingMode, SCHEDULING_MODE } from '../standards/components/scheduling-mode';
import { getCompetencyName } from 'src/utils/competency';
import { formatSiteExternalId } from 'src/utils/format-string';
import { getActivitySaveErrorMessage } from 'src/utils/activity-save-errors';
import { SiteType } from 'src/types/siteType';
import {
  formatNumberToMaxPlaces,
  getMaxDecimalPlacesPattern,
  isNonNegativeDecimalWithMaxPlaces,
  normalizeDecimalInputToMaxPlaces,
  validatePositiveDecimalMaxPlacesInput,
} from 'src/utils/decimal-places';
import { durationHoursToDays } from 'src/utils/duration';

dayjs.extend(utc);

const maxDecimals = 5;

export type NewActivitySchemaType = {
  startDate: string;
  dueDate: string;
  targetDate: string;
  description?: string;
  customerId: number;
  type: ActivityType;
  durationDays: number | string;
  siteId: number | null;
  site: Option | null;
  projectId: number;
  priority?: Priority | number;
  name?: string;
  standardId?: number;
  projectAuditTypeId?: number | null;
  schedulingMode?: SCHEDULING_MODE;
  siteVisitType?: SiteType;
  dependsOnActivities?: ActivityDependencyFormValue[];
};

type Props = {
  id?: number | string;
  open: boolean;
  onClose: () => void;
  project?: ProjectDTO;
  standardId?: number;
  siteId?: number | null;
};

function formatProjectAuditTypeLabel(
  pat: ProjectAuditTypeDTO,
  noneAccreditationBodyLabel: string
): string {
  const name = pat.auditTypeName || pat.auditType?.name || `#${pat.id}`;
  const code = pat.auditTypeCode || pat.auditType?.code;
  const title = code ? `${name} (${code})` : name;
  const scheme = pat.projectStandard?.name;
  const accreditationBody = pat.accreditationBody?.name || noneAccreditationBodyLabel;
  return [title, scheme, accreditationBody].filter(Boolean).join(' · ');
}

const createActivitySchema = (t: (key: string) => string) =>
  zod
    .object({
      startDate: zod.string().min(1, { message: t('form.details.startDate.required') }),
      dueDate: zod.string().min(1, { message: t('form.details.dueDate.required') }),
      targetDate: zod.string().optional(),
      description: zod.string().optional(),
      customerId: zod.number().min(1, { message: t('form.details.customerId.required') }),
      type: zod.enum(['Visit', 'Desk Service'] as const),
      durationDays: zod
        .string()
        .min(1, { message: t('form.details.durationDays.required') })
        .refine((val) => isNonNegativeDecimalWithMaxPlaces(val, maxDecimals), { message: t('form.details.durationDays.required') })
        .transform((val) => parseFloat(val))
        .refine((val) => !Number.isNaN(val) && val >= 0, {
          message: t('form.details.durationDays.required'),
        }),
      siteId: zod.number().nullable().optional(),
      site: zod.any().optional().nullable(),
      projectId: zod.number().optional(),
      priority: zod.enum(Object.values(Priority) as [string, ...string[]]).optional(),
      name: zod.string().min(1, { message: t('form.details.name.required') }),
      projectAuditTypeId: zod.coerce.number().nullable().optional(),
      schedulingMode: zod.enum([SCHEDULING_MODE.AI, SCHEDULING_MODE.MANUAL]).optional(),
      siteVisitType: zod.enum([SiteType.DEFAULT, SiteType.UNANNOUNCED]).optional(),
      dependsOnActivities: zod
        .array(
          zod.object({
            dependsOnActivityId: zod.number(),
            criteria: zod.array(
              zod.object({
                type: zod.enum(['MIN', 'MAX']),
                direction: zod.enum(['BEFORE', 'AFTER']),
                duration: zod.number().int().min(0),
                durationType: zod.enum(['DAY', 'WEEK', 'MONTH', 'YEAR']),
              })
            ),
          })
        )
        .optional(),
    })
    .refine(
      (data) => {
        if (data.type === 'Desk Service') return true;
        return data.siteId && data.siteId > 0;
      },
      { message: t('form.details.siteId.required'), path: ['siteId'] }
    )
    .refine(
      (data) => {
        if (!data.startDate || !data.dueDate) return true;
        const start = dayjs(data.startDate, 'DD/MM/YYYY');
        const end = dayjs(data.dueDate, 'DD/MM/YYYY');
        return start.isBefore(end) || start.isSame(end);
      },
      { message: t('form.validation.startDateBeforeDueDate'), path: ['startDate'] }
    )
    .refine(
      (data) => {
        if (!data.startDate || !data.dueDate) return true;
        const start = dayjs(data.startDate, 'DD/MM/YYYY');
        const end = dayjs(data.dueDate, 'DD/MM/YYYY');
        const daysBetween = end.diff(start, 'days') + 1;
        return daysBetween >= data.durationDays;
      },
      {
        message: t('form.validation.durationExceedsPeriod'),
        path: ['durationDays'],
      }
    );

const formatDate = (date: Date | string | null | undefined) =>
  date ? dayjs(date).format('DD/MM/YYYY') : '';

const formatTargetDate = (date: Date | string | null | undefined) =>
  date ? dayjs(date).format('MMMM YYYY') : '';

const getMonthsBetween = (startDate: string | undefined, endDate: string | undefined) => {
  if (!startDate || !endDate) return [];

  const start = dayjs(startDate, 'DD/MM/YYYY');
  const end = dayjs(endDate, 'DD/MM/YYYY');

  if (!start.isValid() || !end.isValid()) return [];

  const months: string[] = [];
  let current = start.startOf('month');
  const endMonth = end.startOf('month');

  while (current.isBefore(endMonth) || current.isSame(endMonth, 'month')) {
    months.push(current.format('MMMM YYYY'));
    current = current.add(1, 'month');
  }

  return months;
};

const ActivityHeader = ({
  currentActivity,
  isDirty,
  isEditMode,
  onClose,
}: {
  currentActivity?: ActivityDTO | undefined;
  isDirty: boolean;
  isEditMode: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation('activities');
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: !isDirty || !isEditMode ? 3 : 0,
      }}
    >
      <Typography variant="h6">
        {currentActivity ? t('form.header.edit') : t('form.header.new')}
      </Typography>
      <IconButton onClick={onClose} size="medium">
        <Iconify icon="eva:close-fill" />
      </IconButton>
    </Box>
  );
};

const ActivityInfo = ({
  currentActivity,
  project,
}: {
  currentActivity?: ActivityDTO | undefined;
  project?: ProjectDTO;
}) => {
  const { t } = useTranslation('activities');
  const projectAuditType = currentActivity?.projectAuditType;
  const projectAuditTypeLabel = projectAuditType
    ? formatProjectAuditTypeLabel(
        projectAuditType,
        t('form.details.projectAuditType.none')
      )
    : '';

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      gap={1}
      padding={2}
      borderRadius={1}
      bgcolor="background.neutral"
      flexWrap="wrap"
    >
      <CustomLabelWithLink
        label={t('form.details.client')}
        value={currentActivity?.customer?.name || project?.customer?.name || ''}
        link={`clients/${currentActivity?.customer?.id}`}
      />
      <CustomLabelWithLink
        label={t('form.details.project')}
        value={currentActivity?.standard?.project?.id.toString() || project?.id.toString() || ''}
        link={`projects/${currentActivity?.standard?.project?.id || project?.id}`}
      />
      {currentActivity && (
        <CustomLabelWithLink
          label={t('form.details.reference')}
          value={currentActivity?.standard?.project?.orderReference || ''}
        />
      )}
      {currentActivity && (
        <CustomLabelWithLink
          label={t('form.details.id')}
          value={currentActivity?.id.toString() || ''}
        />
      )}
      {projectAuditTypeLabel && (
        <CustomLabelWithLink
          label={t('form.details.projectAuditType.label')}
          value={projectAuditTypeLabel}
        />
      )}
    </Box>
  );
};

const DateFields = ({
  methods,
  errors,
  startDate,
  dueDate,
  availableMonths,
  sites,
  watch,
  siteId,
  onLoadMoreSites,
  onSearchChangeSites,
  siteVisitType
}: any) => {
  const { t } = useTranslation('activities');
  const [announced, setAnnounced] = useState(siteVisitType === SiteType.DEFAULT);
  const handleDateChange = (fieldName: 'startDate' | 'dueDate') => (date: any) => {
    methods.setValue(fieldName, date ? dayjs(date).format('DD/MM/YYYY') : '', {
      shouldValidate: true,
      shouldDirty: true,
    });
    methods.trigger(['startDate', 'durationDays']);
  };
  
  const handleDurationBlur = (e: FocusEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const normalized = normalizeDecimalInputToMaxPlaces(raw, maxDecimals);
    if (normalized !== raw) {
      methods.setValue('durationDays', normalized, { shouldValidate: true, shouldDirty: true });
    }
  };

  const handleSwitchChange = (newValue: boolean) => {
    methods.setValue('siteVisitType', newValue ? SiteType.DEFAULT : SiteType.UNANNOUNCED, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <>
      <Grid item xs={12} md={6}>
        <Stack spacing={2}>
          <Field.Text name="name" label={t('form.details.name.label')} size="small" required />

          <Field.DatePicker
            name="startDate"
            label={t('form.details.startDate.label')}
            slotProps={{
              textField: {
                size: 'small',
                error: !!errors.startDate,
                helperText: errors.startDate?.message,
                required: true,
              },
            }}
            format="DD/MM/YYYY"
            value={startDate ? dayjs(startDate, 'DD/MM/YYYY') : null}
            onChange={handleDateChange('startDate')}
          />
          <Field.Select
            name="targetDate"
            label={t('form.details.targetDate.label')}
            size="small"
            disabled={!startDate || !dueDate || availableMonths.length === 0}
            required
          >
            {availableMonths.map((month: string) => (
              <MenuItem key={month} value={month}>
                {month}
              </MenuItem>
            ))}
          </Field.Select>
          <Field.Text
            name="durationDays"
            label={t('form.details.durationDays.label')}
            inputProps={{ inputMode: 'decimal', pattern: getMaxDecimalPlacesPattern(maxDecimals) }}
            InputProps={{
              endAdornment: t('form.details.durationDays.unit'),
              onKeyPress: (e) => validatePositiveDecimalMaxPlacesInput(e, maxDecimals),
            }}
            onBlur={handleDurationBlur}
            size="small"
            required
          />
        <SchedulingMode name="schedulingMode" label={t('form.details.schedulingMode.label')} fullWidth />
        </Stack>
      </Grid>
      <Grid item xs={12} md={6}>
        <Stack spacing={2}>
          <Field.Select name="priority" label={t('form.details.priority.label')} size="small">
            {Object.values(Priority).map((priority) => (
              <MenuItem key={priority} value={priority}>
                {PRIORITY_MAP[priority]}
              </MenuItem>
            ))}
          </Field.Select>
          <Field.DatePicker
            name="dueDate"
            label={t('form.details.dueDate.label')}
            slotProps={{
              textField: {
                size: 'small',
                error: !!errors.dueDate,
                helperText: errors.dueDate?.message,
                required: true,
              },
            }}
            format="DD/MM/YYYY"
            value={dueDate ? dayjs(dueDate, 'DD/MM/YYYY') : null}
            onChange={handleDateChange('dueDate')}
          />
          <Field.Select name="type" label={t('form.details.type.label')} size="small">
            <MenuItem value="Visit">{t('form.details.type.visit')}</MenuItem>
            <MenuItem value="Desk Service">{t('form.details.type.deskService')}</MenuItem>
          </Field.Select>
          <VirtualizedAutocomplete
            selectedOption={
              watch('site') ??
              (siteId != null
                ? {
                    key: siteId,
                    label: sites.find((site: SiteDTO) => site.id === siteId)?.name || '',
                  }
                : null)
            }
            onChange={(newValue) => {
              methods.setValue('siteId', (newValue?.key as number) ?? null, {
                shouldValidate: true,
                shouldDirty: true,
              });
              methods.setValue('site', newValue ?? null);
            }}
            options={sites.map((site: SiteDTO) => ({
              key: site.id,
              label: `${site.externalId ? `${formatSiteExternalId(site.externalId)} - ` : ''}${site.name}`,
            }))}
            label={t('form.details.site.label')}
            onEndReached={onLoadMoreSites}
            onTextChange={onSearchChangeSites}
            textFieldProps={{
              size: 'small',
              required: watch('type') !== 'Desk Service',
              disabled: watch('type') === 'Desk Service',
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 1 }}>
            <Switch
              checked={announced}
              onChange={(e) => {
                const newValue = e.target.checked;
                setAnnounced(newValue);
                handleSwitchChange(newValue);
              }}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'primary.main',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'primary.main',
                },
              }}
            />
            <Chip
              label={
                announced
                  ? t('form.details.visitType.announced')
                  : t('form.details.visitType.unannounced')
              }
              size="small"
              sx={{
                height: 24,
                fontSize: '12px',
                fontWeight: 500,
                bgcolor: announced ? 'primary.lighter' : 'grey.200',
                color: announced ? 'primary.darker' : 'text.secondary',
                '&:hover': {
                  bgcolor: announced ? 'primary.lighter' : 'grey.200',
                  color: announced ? 'primary.darker' : 'text.secondary',
                },
              }}
            />
          </Box>
        </Stack>
      </Grid>
    </>
  );
};

export function ActivityNewEditForm({ id, open, onClose, project, standardId, siteId }: Props) {
  const { t } = useTranslation(['activities', 'common']);
  const { user } = useAuthContext();
  const { visit: currentActivity, visitLoading, mutateVisit } = useGetVisit(id?.toString() || '');
  const customerId = currentActivity?.customer?.id || project?.customer?.id;

  const projectOwnerId =
    currentActivity?.standard?.project?.owner?.id ?? project?.owner?.id;
  const canUpdateOwnedActivity = useCanUpdateOwnedActivity(projectOwnerId);
  const canEditDependencies = !!currentActivity && canUpdateOwnedActivity;
  const projectId = currentActivity?.standard?.project?.id ?? project?.id;
  const isEditMode = !!currentActivity;

  const [projectAuditTypes, setProjectAuditTypes] = useState<ProjectAuditTypeDTO[]>([]);
  const [projectAuditTypesLoading, setProjectAuditTypesLoading] = useState(false);
  const [projectAuditTypesLoadError, setProjectAuditTypesLoadError] = useState(false);

  useEffect(() => {
    if (!open || isEditMode || !project?.id) {
      setProjectAuditTypes([]);
      setProjectAuditTypesLoadError(false);
      return;
    }

    let cancelled = false;
    setProjectAuditTypesLoading(true);
    setProjectAuditTypesLoadError(false);
    const auditTypesUrl =
      standardId != null
        ? `${endpoints.projects.auditTypes(project.id)}?standardId=${standardId}`
        : endpoints.projects.auditTypes(project.id);
    axios
      .get<ProjectAuditTypeDTO[]>(auditTypesUrl)
      .then((response) => {
        if (!cancelled) {
          setProjectAuditTypes(response.data || []);
          setProjectAuditTypesLoadError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProjectAuditTypes([]);
          setProjectAuditTypesLoadError(true);
          toast.error(t('form.error.save'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setProjectAuditTypesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, isEditMode, project?.id, standardId, t]);

  const requireProjectAuditType = !isEditMode && projectAuditTypes.length > 0;

  const {
    paginatedData: sites,
    onLoadMore: onLoadMoreSites,
    onSearchChange: onSearchChangeSites,
  } = useListForFilter<SiteDTO>({
    endpoint: endpoints.site.root,
    additionalParams: {
      'select-fields': 'id,name,externalId',
      'customer-ids': customerId?.toString() || '',
      'active-only': 'true',
    },
    initialData: currentActivity?.site,
  });
  const isStatusOpen = currentActivity?.status === ActivityStatus.CREATED;

  const defaultValues = useMemo<Partial<NewActivitySchemaType>>(() => {
    const base: Partial<NewActivitySchemaType> = {
      startDate: formatDate(currentActivity?.startDate) || '',
      dueDate: formatDate(currentActivity?.dueDate) || '',
      targetDate: formatTargetDate(currentActivity?.targetDate) || '',
      description: currentActivity?.description || '',
      customerId: currentActivity?.customer?.id || 1,
      type: currentActivity?.type || ('Visit' as ActivityType),
      durationDays: (() => {
        if (currentActivity?.durationDays != null) {
          const n = Number(currentActivity.durationDays);
          if (Number.isFinite(n)) {
            return formatNumberToMaxPlaces(n, maxDecimals);
          }
          return currentActivity.durationDays.toString();
        }
        if (currentActivity?.duration != null) {
          const hours = Number(currentActivity.duration);
          if (Number.isFinite(hours)) {
            return formatNumberToMaxPlaces(durationHoursToDays(hours), maxDecimals);
          }
        }
        return '1';
      })(),
      siteId: currentActivity?.site?.id || siteId || null,
      site: currentActivity?.site
        ? {
            key: currentActivity.site.id,
            label: `${currentActivity.site.externalId ? `${formatSiteExternalId(currentActivity.site.externalId)} - ` : ''}${currentActivity.site.name}`,
          }
        : null,
      name: currentActivity?.name || '',
      priority: currentActivity?.priority || Priority.NORMAL,
      projectAuditTypeId: currentActivity?.projectAuditTypeId ?? null,
      schedulingMode: currentActivity?.schedulingMode || SCHEDULING_MODE.AI,
      siteVisitType: currentActivity?.siteVisitType || SiteType.DEFAULT,
      dependsOnActivities:
        currentActivity?.dependsOnActivities?.map((a) => ({
          dependsOnActivityId: a.id,
          criteria: a.criteria ?? [],
        })) ?? [],
    };
    return base;
  }, [currentActivity, siteId]);

  const methods = useForm<NewActivitySchemaType>({
    resolver: zodResolver(createActivitySchema(t)),
    defaultValues,
    mode: 'onChange',
  });

  const {
    reset,
    handleSubmit,
    watch,
    formState: { isSubmitting, isDirty, isValid, errors },
  } = methods;

  const [startDate, dueDate, activityType] = watch(['startDate', 'dueDate', 'type']);
  const dependsOnActivities = watch('dependsOnActivities');
  const availableMonths = useMemo(() => getMonthsBetween(startDate, dueDate), [startDate, dueDate]);

  useEffect(() => {
    if (activityType === 'Desk Service') {
      methods.setValue('siteId', null, { shouldValidate: true });
      methods.setValue('site', null);
    }
  }, [activityType, methods]);

  useEffect(() => {
    if (currentActivity) {
      reset(defaultValues);
    }
  }, [currentActivity, defaultValues, reset]);

  useEffect(() => {
    if (open) {
      mutateVisit();
    }
    reset(defaultValues);
  }, [open, reset, defaultValues, mutateVisit]);

  const handleClose = () => {
    onClose();
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (isEditMode) {
        const { dirtyFields } = methods.formState;
        const submissionData: Partial<NewActivitySchemaType> = {};

        Object.keys(dirtyFields).forEach((key) => {
          const fieldKey = key as keyof NewActivitySchemaType;
          const value = data[fieldKey];

          if (fieldKey === 'startDate' || fieldKey === 'dueDate') {
            const dateObj = dayjs.utc(value as string, 'DD/MM/YYYY');
            if (!dateObj.isValid()) {
              throw new Error('Invalid date format');
            }
            submissionData[fieldKey] = dateObj.format('DD/MM/YYYY') as any;
          } else if (fieldKey === 'targetDate') {
            if (dayjs(value as string, 'MMMM YYYY', true).isValid()) {
              submissionData[fieldKey] = value as any;
            } else {
              submissionData[fieldKey] = dayjs
                .utc(value as string, 'MMMM YYYY')
                .format('MMMM YYYY') as any;
            }
          } else if (fieldKey === 'priority') {
            submissionData[fieldKey] = PRIORITY_VALUES_MAP[value as Priority];
          } else if (fieldKey === 'projectAuditTypeId') {
            // Project audit type is set at create time only
            return;
          } else {
            submissionData[fieldKey] = value as any;
          }
        });

        const apiSubmissionData = {
          ...submissionData,
          startDate: submissionData.startDate
            ? dayjs.utc(submissionData.startDate, 'DD/MM/YYYY').toDate()
            : undefined,
          dueDate: submissionData.dueDate
            ? dayjs.utc(submissionData.dueDate, 'DD/MM/YYYY').toDate()
            : undefined,
          targetDate: submissionData.targetDate
            ? dayjs.utc(submissionData.targetDate, 'MMMM YYYY').toDate()
            : undefined,
        };

        await axios.put(
          `${endpoints.activities.update}/${currentActivity.id}?clientId=${user?.client?.id}`,
          apiSubmissionData
        );

        // Invalidate the activity cache so modal shows fresh data
        await mutate(endpoints.activities.details(currentActivity.id.toString()));

        toast.success(t('form.success.update'));
      } else {
        if (projectAuditTypesLoadError) {
          toast.error(t('form.error.save'));
          return;
        }

        if (requireProjectAuditType && (data.projectAuditTypeId == null || data.projectAuditTypeId <= 0)) {
          methods.setError('projectAuditTypeId', {
            type: 'required',
            message: t('form.details.projectAuditType.required'),
          });
          return;
        }

        const newActivityData = {
          ...data,
          startDate: dayjs.utc(data.startDate, 'DD/MM/YYYY').toDate(),
          dueDate: dayjs.utc(data.dueDate, 'DD/MM/YYYY').toDate(),
          targetDate: data.targetDate
            ? dayjs.utc(data.targetDate, 'MMMM YYYY').toDate()
            : undefined,
          projectId: project?.id,
          priority: PRIORITY_VALUES_MAP[data.priority as Priority],
          standardId,
          customerId: project?.customer?.id,
          projectAuditTypeId: data.projectAuditTypeId ?? undefined,
        };

        await axios.post(
          `${endpoints.activities.create}?clientId=${user?.client?.id}`,
          newActivityData
        );
        trackEvent(AnalyticsEvent.activity.created, {
          has_standard: Boolean(standardId),
          priority: (data.priority as Priority) ?? Priority.NORMAL,
        });
        toast.success(t('form.success.create'));
      }

      onClose();
    } catch (error) {
      console.error(error);
      toast.error(getActivitySaveErrorMessage(error, t, 'form.error.save'));
    }
  });

  if (visitLoading) return <SplashScreen />;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '40%',
          maxWidth: 800,
          bgcolor: 'background.paper',
          borderRadius: 1,
          p: 3,
          maxHeight: '70vh',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <ActivityHeader
          currentActivity={currentActivity}
          isDirty={isDirty}
          isEditMode={isEditMode}
          onClose={handleClose}
        />

        <Form methods={methods} onSubmit={onSubmit}>
          {isEditMode &&
            isDirty &&
            !isStatusOpen &&
            Object.keys(methods.formState.dirtyFields).some((field) => field !== 'description') && (
              <Alert severity="error" sx={{ backgroundColor: 'transparent' }}>
                <Typography variant="body2">{t('form.changesAlert')}</Typography>
              </Alert>
            )}
          <Stack spacing={3}>
            <ActivityInfo currentActivity={currentActivity} project={project} />

            <Grid container spacing={2}>
              {!isEditMode && projectAuditTypes.length > 0 && (
                <Grid item xs={12}>
                  <Field.Select
                    name="projectAuditTypeId"
                    label={t('form.details.projectAuditType.label')}
                    size="small"
                    required
                    disabled={projectAuditTypesLoading}
                    helperText={t('form.details.projectAuditType.helper')}
                  >
                    {projectAuditTypes.map((pat) => (
                      <MenuItem key={pat.id} value={pat.id}>
                        {formatProjectAuditTypeLabel(
                          pat,
                          t('form.details.projectAuditType.none')
                        )}
                      </MenuItem>
                    ))}
                  </Field.Select>
                </Grid>
              )}

              <DateFields
                methods={methods}
                errors={errors}
                startDate={startDate}
                dueDate={dueDate}
                availableMonths={availableMonths}
                sites={sites}
                watch={watch}
                siteId={siteId}
                onLoadMoreSites={onLoadMoreSites}
                onSearchChangeSites={onSearchChangeSites}
                siteVisitType={currentActivity?.siteVisitType || SiteType.DEFAULT}
              />

              <Grid item xs={12}>
                <Field.Text
                  name="description"
                  label={t('form.details.description.label')}
                  multiline
                  size="small"
                />
              </Grid>

              <ActivityDependenciesField
                projectId={projectId}
                currentActivityId={currentActivity?.id}
                value={dependsOnActivities ?? []}
                onChange={(deps) =>
                  methods.setValue('dependsOnActivities', deps, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                canEdit={canEditDependencies}
              />
            </Grid>

            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button variant="outlined" onClick={handleClose}>
                {t('common:actions.cancel')}
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isSubmitting}
                disabled={
                  isEditMode
                    ? !isDirty || !isValid
                    : !isValid || projectAuditTypesLoading || projectAuditTypesLoadError
                }
              >
                {isEditMode ? t('form.buttons.save') : t('form.buttons.create')}
              </LoadingButton>
            </Stack>
          </Stack>
        </Form>
      </Box>
    </Modal>
  );
}
